import {
  pgTable,
  uuid,
  varchar,
  text,
  timestamp,
  pgEnum,
  jsonb,
  customType,
  boolean,
  integer,
  index,
  uniqueIndex,
  primaryKey,
  real,
  date,
  time,
  check,
} from "drizzle-orm/pg-core";
import { relations, sql } from "drizzle-orm";

/**
 * Custom PostGIS geometry type that properly specifies geometry type and SRID
 *
 * IMPORTANT: No space in format - use `geometry(Point,4326)` not `geometry(Point, 4326)`
 * PostgreSQL stores it without the space, and Drizzle will detect a false schema
 * difference if we include the space, causing unnecessary data truncation warnings.
 */
const geometryColumn = (
  name: string,
  geometryType: "Point" | "Polygon" | "LineString" | "Geometry" = "Geometry",
  srid: number = 4326,
) => {
  return customType<{
    data: { x: number; y: number } | null;
    driverData: string;
  }>({
    dataType() {
      // No space between geometryType and srid - matches PostgreSQL's canonical format
      // This prevents Drizzle from detecting false schema differences
      return `geometry(${geometryType},${srid})`;
    },
    toDriver(value) {
      if (!value) {
        return sql`NULL`;
      }
      // Convert {x, y} to PostGIS format
      return sql`ST_SetSRID(ST_MakePoint(${value.x}, ${value.y}), ${srid})`;
    },
    fromDriver(value) {
      if (!value) return null;
      // Parse geometry from database - this is a simplified version
      // In production, you'd use ST_AsGeoJSON and parse the result
      return value as unknown as { x: number; y: number };
    },
  })(name);
};

// Enums
export const mapVersionStatusEnum = pgEnum("map_version_status", [
  "draft",
  "published",
]);

export const layerTypeEnum = pgEnum("layer_type", [
  // Polygon/Line layers (visual geometry)
  "boundary",
  "path_primary",
  "path_secondary",
  "path_tertiary",
  "path_wide",
  "bridge",
  "tunnel",
  "building",
  "building_primary", // Important landmarks, restaurants, main attractions
  "building_secondary", // Background structures, vendors, service buildings
  "building_entrance", // Entrance buildings, gates
  "barrier",
  "vegetation",
  "water",
  "waterway", // Linear water (rivers, creeks, streams) - line layer
  "landscape",
  "rail",
  "bus",
  "parking",
  "zone",
  "custom_polygon",
  "custom_path",
  // Special layers
  "vignette", // Boundary mask overlay
  "prop", // Decorative scatter (trees, lamps - via stamp tool, NOT OSM import)
]);

export const memberRoleEnum = pgEnum("member_role", [
  "owner",
  "editor",
  "viewer",
]);

// Org types - exported as const array for reuse in zod schemas
export const ORG_TYPES = ["zoo", "garden", "fair", "park", "default"] as const;
export const orgTypeEnum = pgEnum("org_type", ORG_TYPES);

// Org tiers - subscription levels for feature gating
export const ORG_TIERS = ["base", "pro", "premium", "enterprise"] as const;
export const organizationTierEnum = pgEnum("organization_tier", ORG_TIERS);

// POI status - "normal" derives open/closed from hours schedule
// "temp_closed" and "seasonal" are manual overrides
export const poiStatusEnum = pgEnum("poi_status", [
  "normal",
  "temp_closed",
  "seasonal",
]);

// Sponsor tiers for featured/promoted POIs (premium+ feature)
// NOTE: This enum is for POI pin sponsor rings only — NOT for map-level sponsor tiers
export const SPONSOR_TIERS = ["bronze", "silver", "gold"] as const;
export type SponsorTier = (typeof SPONSOR_TIERS)[number];
export const sponsorTierEnum = pgEnum("sponsor_tier", SPONSOR_TIERS);

// Size buckets for map-level sponsor logo display
export const SIZE_BUCKETS = ["large", "medium", "small"] as const;
export type SizeBucket = (typeof SIZE_BUCKETS)[number];
export const sizeBucketEnum = pgEnum("size_bucket", SIZE_BUCKETS);

/** Resolved sponsor entry returned by the visitor API (logo URL already built) */
export interface SponsorEntry {
  id: string;
  name: string;
  logoUrl: string | null;
  websiteUrl: string | null;
  isHeadline: boolean;
  showName: boolean;
  linkedPoiIds: string[];
  linkedPoiCount: number;
  linkedEventCount: number;
  linkedLabelCount: number;
}

/** Dynamic tier group — visitor payload shape */
export interface SponsorTierGroup {
  name: string;
  slug: string;
  sortOrder: number;
  sizeBucket: SizeBucket;
  color: string | null;
  showInSplash: boolean;
  showInWelcome: boolean;
  showInHamburger: boolean;
  maxInSplash: number;
  maxInWelcome: number;
  sponsors: SponsorEntry[];
}

/** Sponsor data embedded in POI/event visitor payloads */
export interface SponsorRef {
  sponsorId: string;
  sponsorName: string;
  sponsorShowName: boolean;
  sponsorLogoUrl: string | null;
  sponsorTierColor: string | null;
  sponsorTierSlug: string;
  sponsorTierSortOrder: number;
}

// Subscription payment status (managed by Stripe webhooks, NOT tier)
export const SUBSCRIPTION_STATUSES = [
  "building",
  "active",
  "trialing",
  "past_due",
  "canceled",
  "unpaid",
] as const;
export const subscriptionStatusEnum = pgEnum(
  "subscription_status",
  SUBSCRIPTION_STATUSES,
);

// Map size presets (controls zoom-dependent rendering offset)
export const MAP_SIZES = ["small", "medium", "large"] as const;
export const mapSizeEnum = pgEnum("map_size", MAP_SIZES);

export const MAP_STATUSES = ["active", "paused"] as const;
export type MapStatus = (typeof MAP_STATUSES)[number];
export const mapStatusEnum = pgEnum("map_status", MAP_STATUSES);

export const operatingModeEnum = pgEnum("operating_mode", [
  "always_open",
  "year_round",
  "seasonal",
]);

// Event schedule types
// - single: One specific date/time
// - daily: Every day (within date range if set)
// - weekly: Specific days of week
// - permanent: Ongoing, no end date (V1.1)
export const eventScheduleTypeEnum = pgEnum("event_schedule_type", [
  "single",
  "daily",
  "weekly",
  "permanent",
]);

// Notification types for visitor-facing operational messages
export const NOTIFICATION_TYPES = ["info", "warning", "urgent"] as const;
export const notificationTypeEnum = pgEnum(
  "notification_type",
  NOTIFICATION_TYPES,
);

// Emergency action types for visitor safety features
export const EMERGENCY_ACTION_TYPES = ["call", "navigate", "category"] as const;
export const emergencyActionTypeEnum = pgEnum(
  "emergency_action_type",
  EMERGENCY_ACTION_TYPES,
);

export const auditActionEnum = pgEnum("audit_action", [
  "create",
  "update",
  "delete",
  "publish",
  "restore",
]);

// =============================================================================
// Theme Tables
// =============================================================================

// Base themes - system-owned, read-only templates
export const baseThemes = pgTable("base_themes", {
  id: uuid("id").primaryKey().defaultRandom(),
  slug: varchar("slug", { length: 50 }).notNull().unique(),
  name: varchar("name", { length: 100 }).notNull(),
  config: jsonb("config").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Themes - org customizations on top of base themes
export const themes = pgTable("themes", {
  id: uuid("id").primaryKey().defaultRandom(),
  orgId: uuid("org_id").references(() => organizations.id, {
    onDelete: "cascade",
  }),
  baseThemeId: uuid("base_theme_id")
    .notNull()
    .references(() => baseThemes.id),
  name: varchar("name", { length: 100 }).notNull(),
  slug: varchar("slug", { length: 50 }).notNull(),
  customizations: jsonb("customizations").default(sql`'{}'::jsonb`),
  isActive: boolean("is_active").default(true).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const baseThemesRelations = relations(baseThemes, ({ many }) => ({
  themes: many(themes),
}));

export const themesRelations = relations(themes, ({ one, many }) => ({
  baseTheme: one(baseThemes, {
    fields: [themes.baseThemeId],
    references: [baseThemes.id],
  }),
  organization: one(organizations, {
    fields: [themes.orgId],
    references: [organizations.id],
  }),
  maps: many(maps),
}));

// =============================================================================
// Visitor Menu Config Types
// =============================================================================

export interface MenuItem {
  id: string;
  label: string;
  labelTranslations?: Record<string, string> | null;
  url: string;
  icon: string;
  open_in_new_tab: boolean;
  sort_order: number;
  is_cta?: boolean;
}

export interface MenuSection {
  id: string;
  title: string;
  titleTranslations?: Record<string, string> | null;
  sort_order: number;
  items: MenuItem[];
}

export interface SocialLinks {
  facebook: string | null;
  instagram: string | null;
  twitter: string | null;
  youtube: string | null;
  tiktok: string | null;
  linkedin: string | null;
}

export interface VisitorMenuConfig {
  sections: MenuSection[];
  social_links: SocialLinks;
}

export interface OnboardingChecklist {
  org_profile_completed?: boolean;
  qr_downloaded?: boolean;
  analytics_viewed?: boolean;
  checklist_dismissed?: boolean;
}

export interface MetricsEmailConfig {
  enabled: boolean;
  frequency: "weekly" | "monthly";
  dayOfWeek?: number; // 0=Sun..6=Sat (weekly only)
  dayOfMonth?: number; // 1-28 (monthly only)
  excludedMemberIds?: string[]; // member IDs excluded by org admin
}

export interface OrgLimits {
  maxAdminSeats?: number | null;
  maxMaps?: number | null;
  poiMediaLimit?: number | null;
  auditRetentionDays?: number | null;
}

// =============================================================================
// FAQ Config Types
// =============================================================================

export interface FAQItem {
  id: string;
  categoryId?: string | null;
  question: string;
  questionTranslations?: Record<string, string> | null;
  answer: unknown; // Lexical JSON AST
  answerTranslations?: Record<string, unknown> | null;
  sort_order: number;
}

export interface FAQCategory {
  id: string;
  title: string;
  titleTranslations?: Record<string, string> | null;
  enabled: boolean;
  sort_order: number;
}

export interface FAQConfig {
  enabled: boolean;
  title: string;
  titleTranslations?: Record<string, string> | null;
  items: FAQItem[];
  categories: FAQCategory[];
}

/**
 * Operator-supplied figures behind a sponsor pitch report (plan 127 C).
 *
 * Every number here is typed in by the venue from their OWN systems — ticket
 * scans, POS, web analytics. None of it comes from Wondermaps, because a venue
 * pitching next season's sponsors has no map traffic yet.
 *
 * The assumption fields are the honest part: we have no data on what share of
 * visitors open the map, so the venue sets it and the report shows the
 * arithmetic rather than asserting a result. Defaults are a starting point the
 * operator is expected to overwrite, not a Wondermaps benchmark.
 */
export interface SponsorPitchInputs {
  // --- What the venue measured last season ---
  seasonVisitors: number;
  operatingDays?: number | null;
  concessionRevenue?: number | null; // whole currency units, operator-entered
  websiteViews?: number | null;
  socialFollowers?: number | null;
  /** Anything the venue tracks that we didn't anticipate. Rendered as-is. */
  customMetrics?: { label: string; value: string }[];

  // --- Assumptions driving the projection (operator-editable) ---
  /** % of visitors expected to open the map at all. */
  mapAdoptionPct: number;
  /** % of map openers who see the placement being pitched. */
  placementViewPct: number;

  // --- What is being offered ---
  slotPriceCents?: number | null; // snapshot; tier price may drift after sending
  notes?: string | null;
}

export interface BrandColorConfig {
  // Existing foreground-accent toggles
  categoryPills?: boolean;
  drawerHandle?: boolean;
  searchHeaderIcons?: boolean;
  directionsUI?: boolean;
  poiCtaButton?: boolean;
  poweredByFooter?: boolean;

  // New foreground-accent toggles (PR5)
  // Note: menuAccents removed in PR5 hard cutover — replaced by discrete slots below
  bottomNavActive?: boolean;
  morePageCtaSecondary?: boolean;
  menuIcons?: boolean;
  socialCardBranded?: boolean;

  // New background-tint opacity sliders (0–100; null/undefined = off; default 16 on enable)
  socialCardBgOpacity?: number | null;
}

export interface OrgSettings {
  visitor_menu?: VisitorMenuConfig;
  show_attribution?: boolean;
  onboarding_checklist?: OnboardingChecklist;
  metricsEmail?: MetricsEmailConfig;
  /** Languages available in picker (subset of supported). Defaults to all. */
  availableLanguages?: ("en" | "es" | "fr")[];
  /** Default language when user has no preference. Defaults to "en". */
  defaultLanguage?: "en" | "es" | "fr";
  limits?: OrgLimits;
  faq?: FAQConfig;
}

// =============================================================================
// POI Rich Content Types
// =============================================================================

export interface POIPhoto {
  id: string;
  cloudinaryPublicId: string;
  cloudinaryVersion: string;
  altText: string | null;
  sortOrder: number;
}

export interface DaySchedule {
  day: 0 | 1 | 2 | 3 | 4 | 5 | 6; // Sun=0, Sat=6
  open: string; // "09:00"
  close: string; // "17:00"
  closed: boolean;
}

export interface HoursConfig {
  type: "always_open" | "scheduled" | "variable";
  schedule?: DaySchedule[]; // if type === "scheduled"
  note?: string; // "Closed for winter", etc.
}

export interface POILink {
  id: string;
  label: string; // max 30 chars
  url: string;
  icon?: string; // FA icon name
  isPrimary: boolean; // only one can be true
  sortOrder: number;
}

// Season date range for seasonal POIs (MM-DD format)
// If end < start, season wraps across year boundary (e.g., Nov 15 - Mar 15)
export interface SeasonDates {
  start: string; // "MM-DD" format
  end: string; // "MM-DD" format
}

// =============================================================================
// Map Operating Hours Types
// =============================================================================

export type OperatingMode = "always_open" | "year_round" | "seasonal";

export interface ClosureDate {
  date: string; // "YYYY-MM-DD" (specific) or "MM-DD" (recurring)
  recurring?: boolean; // true = MM-DD matched every year
  label?: string; // "Christmas Day"
}

export interface DateOverride {
  date: string; // "YYYY-MM-DD"
  open: string; // "10:00"
  close: string; // "14:00"
  label?: string; // "Holiday Hours"
}

export interface OperatingConfig {
  seasonStart?: string; // "MM-DD" (seasonal only)
  seasonEnd?: string; // "MM-DD" (seasonal only)
  operatingDays?: number[]; // [0,5,6] = Sun/Fri/Sat. null = every day
  defaultOpen?: string; // "09:00"
  defaultClose?: string; // "17:00"
  dayOverrides?: { day: number; open: string; close: string }[];
  closureDates?: ClosureDate[];
  dateOverrides?: DateOverride[];
}

export interface OperatingStatusMessage {
  title?: string;
  body?: string;
}

export interface OperatingStatusMessages {
  closed_season?: OperatingStatusMessage;
  closed_holiday?: OperatingStatusMessage;
  closed_today?: OperatingStatusMessage;
  closed_hours?: OperatingStatusMessage;
}

// =============================================================================
// POI Accessibility Attributes
// =============================================================================

/** Dietary options for food POIs */
export const DIETARY_OPTIONS = [
  "vegetarian",
  "vegan",
  "gluten_free",
  "kosher",
  "halal",
] as const;
export type DietaryOption = (typeof DIETARY_OPTIONS)[number];

/**
 * Accessibility attributes stored in POI propertiesJson.
 * Used by overlay system to filter/highlight POIs for visitors with
 * accessibility, sensory, or practical needs.
 *
 * Key insight: POI attributes ≠ overlay membership
 * One attribute can trigger multiple overlays (e.g., wheelchair_accessible
 * appears in both "Wheelchair" and "Mobility" overlays).
 */
export interface POIAccessibilityAttributes {
  // Mobility
  wheelchair_accessible?: boolean;
  wheelchair_restroom?: boolean;
  wheelchair_rental?: boolean;
  scooter_rental?: boolean;
  ramp_available?: boolean;
  wide_entrance?: boolean;
  elevator?: boolean;
  rest_area?: boolean;

  // Sensory
  quiet_zone?: boolean;
  sensory_break_room?: boolean;
  hearing_loop?: boolean;
  braille_signage?: boolean;

  // Family
  changing_station?: boolean;
  nursing_room?: boolean;
  family_restroom?: boolean;
  stroller_parking?: boolean;
  kid_friendly?: boolean;

  // Service animals
  service_animal_relief?: boolean;
  pet_water_station?: boolean;

  // Dietary (array for food POIs)
  dietary_options?: DietaryOption[];

  // Environment
  shaded_area?: boolean;
  covered_area?: boolean;
}

// Organizations - Multi-tenant support
export const organizations = pgTable("organizations", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 255 }).notNull(),
  slug: varchar("slug", { length: 100 }).notNull().unique(),
  description: text("description"),
  // Venue type for category filtering (nullable for backward compat)
  type: orgTypeEnum("type").default("default"),
  // Subscription tier for feature gating
  tier: organizationTierEnum("tier").default("base").notNull(),
  // Branding fields for whitelabeling
  logoPublicId: varchar("logo_public_id", { length: 255 }),
  logoVersion: varchar("logo_version", { length: 50 }),
  // Square mark/favicon logo (separate from full horizontal logo)
  logomarkPublicId: varchar("logomark_public_id", { length: 255 }),
  logomarkVersion: varchar("logomark_version", { length: 50 }),
  brandColor: varchar("brand_color", { length: 7 }),
  splashBackground: varchar("splash_background", { length: 7 }),
  appName: varchar("app_name", { length: 100 }),
  hidePoweredBy: boolean("hide_powered_by").default(false),
  brandColorConfig: jsonb("brand_color_config").$type<BrandColorConfig>(),
  // IANA timezone for event display (e.g., "America/New_York")
  timezone: varchar("timezone", { length: 64 })
    .default("America/New_York")
    .notNull(),
  // Flexible settings JSONB (visitor menu config, etc.)
  settings: jsonb("settings").$type<OrgSettings>().default({}),
  // Single org-wide shareable invite link
  inviteCode: varchar("invite_code", { length: 20 }),
  inviteCodeCreatedAt: timestamp("invite_code_created_at"),
  // REMOVED: qrRedirectOverride — replaced by per-channel redirectMode on qrCodes table
  // Stripe billing (managed by webhooks — never touches tier)
  stripeCustomerId: varchar("stripe_customer_id", { length: 255 }).unique(),
  subscriptionStatus: subscriptionStatusEnum("subscription_status")
    .default("building")
    .notNull(),
  currentPeriodEnd: timestamp("current_period_end"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const organizationsRelations = relations(organizations, ({ many }) => ({
  maps: many(maps),
  members: many(organizationMembers),
  tags: many(poiTags),
  categories: many(poiCategories),
  visitorFeedback: many(visitorFeedback),
  slugHistory: many(orgSlugHistory),
}));

// Org Slug History - preserves old slugs as permanent redirects (QR codes, bookmarks)
export const orgSlugHistory = pgTable("org_slug_history", {
  id: uuid("id").primaryKey().defaultRandom(),
  orgId: uuid("org_id")
    .notNull()
    .references(() => organizations.id, { onDelete: "cascade" }),
  slug: varchar("slug", { length: 100 }).notNull().unique(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const orgSlugHistoryRelations = relations(orgSlugHistory, ({ one }) => ({
  organization: one(organizations, {
    fields: [orgSlugHistory.orgId],
    references: [organizations.id],
  }),
}));

// Maps - Top-level container
export const maps = pgTable(
  "maps",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    organizationId: uuid("organization_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    themeId: uuid("theme_id").references(() => themes.id),
    name: varchar("name", { length: 255 }).notNull(),
    // URL-friendly identifier, unique within org (e.g., "main-campus", "north-wing")
    slug: varchar("slug", { length: 100 }).notNull(),
    description: text("description"),
    // Default map center for initial load (before fitBounds adjusts)
    defaultCenter: geometryColumn("default_center", "Point"),
    // Default zoom level (typically 14-17 for venue maps)
    defaultZoom: integer("default_zoom").default(15),
    // Map size preset — controls zoom offset for all zoom-dependent rendering
    mapSize: mapSizeEnum("map_size").notNull().default("small"),
    // Event period for festivals/fairs (enables "Day X of Y" display)
    eventPeriodStart: date("event_period_start"),
    eventPeriodEnd: date("event_period_end"),
    eventPeriodName: varchar("event_period_name", { length: 100 }),
    // Operating hours mode and config
    operatingMode: operatingModeEnum("operating_mode")
      .notNull()
      .default("year_round"),
    operatingConfig: jsonb("operating_config").$type<OperatingConfig | null>(),
    // Custom messages for operating status banners (overrides i18n defaults)
    operatingMessages: jsonb(
      "operating_messages",
    ).$type<OperatingStatusMessages | null>(),
    // Whether this is the default map for the org (shown when no map slug specified)
    // Partial unique index (one default per org) created manually via SQL:
    // CREATE UNIQUE INDEX maps_org_default_unique ON maps (organization_id) WHERE is_default = true;
    isDefault: boolean("is_default").notNull().default(false),
    // Schedule fields (inline, one schedule per map variant)
    isScheduleActive: boolean("is_schedule_active").notNull().default(true),
    scheduleStartDate: date("schedule_start_date"),
    scheduleEndDate: date("schedule_end_date"),
    scheduleStartTime: time("schedule_start_time"),
    scheduleEndTime: time("schedule_end_time"),
    scheduleDaysOfWeek: jsonb("schedule_days_of_week").$type<number[] | null>(),
    // Share image for OG previews (per-map, admin-uploaded)
    shareImagePublicId: varchar("share_image_public_id", { length: 255 }),
    shareImageVersion: varchar("share_image_version", { length: 50 }),
    // Emergency & Safety quick-action buttons
    emergencyActionsEnabled: boolean("emergency_actions_enabled")
      .notNull()
      .default(false),
    // Pause map state — superadmin-only toggle. When paused, visitor sees
    // co-branded splash; admins/superadmins unaffected. See docs/plans/122.
    status: mapStatusEnum("status").notNull().default("active"),
    pausedAt: timestamp("paused_at"),
    pausedBy: text("paused_by").references(() => users.id, {
      onDelete: "set null",
    }),
    // Updated when visitor request arrives w/ ?embed=true or iframe referer.
    // Rate-limited to once/24h. Surfaced as embed-warning on pause confirm.
    lastEmbedRequestAt: timestamp("last_embed_request_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    // Unique constraint: slug must be unique within an organization
    {
      name: "maps_org_slug_unique",
      columns: [table.organizationId, table.slug],
      unique: true,
    },
  ],
);

export const mapsRelations = relations(maps, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [maps.organizationId],
    references: [organizations.id],
  }),
  theme: one(themes, {
    fields: [maps.themeId],
    references: [themes.id],
  }),
  pausedByUser: one(users, {
    fields: [maps.pausedBy],
    references: [users.id],
  }),
  versions: many(mapVersions),
  labels: many(mapLabels),
  events: many(events),
  contentAuditLogs: many(contentAuditLog),
  poiOverrides: many(mapPoiOverrides),
  visitorFeedback: many(visitorFeedback),
  curatedLists: many(curatedLists),
  mapSponsors: many(mapSponsors),
  notifications: many(notifications),
  emergencyActions: many(mapEmergencyActions),
  slugHistory: many(mapSlugHistory),
}));

// Map Slug History - preserves old map slugs as permanent redirects (printed QR codes, signage)
// Unique on (organizationId, oldSlug) — slug is org-scoped, so collisions are only possible within the same org.
export const mapSlugHistory = pgTable(
  "map_slug_history",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    organizationId: uuid("organization_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    oldSlug: varchar("old_slug", { length: 100 }).notNull(),
    retiredAt: timestamp("retired_at").defaultNow().notNull(),
  },
  (table) => [
    {
      name: "map_slug_history_org_slug_unique",
      columns: [table.organizationId, table.oldSlug],
      unique: true,
    },
  ],
);

export const mapSlugHistoryRelations = relations(mapSlugHistory, ({ one }) => ({
  map: one(maps, {
    fields: [mapSlugHistory.mapId],
    references: [maps.id],
  }),
  organization: one(organizations, {
    fields: [mapSlugHistory.organizationId],
    references: [organizations.id],
  }),
}));

// Map POI Overrides - Per-variant POI visibility and field overrides

/**
 * Typed overrides for POI fields on a per-map-variant basis.
 *
 * Convention:
 * - **absent key** → field is not overridden; inherit from source POI
 * - **present key** → field is overridden with the given value
 * - **empty string (`""`)** → field is explicitly cleared (show nothing)
 */
export interface PoiFieldOverrides {
  name?: string;
  description?: string;
  shortDescription?: string;
  hours?: HoursConfig | null;
  phone?: string;
  website?: string;
  menuUrl?: string;
  socialLinks?: POILink[];
  otherLinks?: POILink[];
  categoryId?: string;
}

export const mapPoiOverrides = pgTable(
  "map_poi_overrides",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    sourcePoiId: uuid("source_poi_id")
      .notNull()
      .references(() => pois.id, { onDelete: "cascade" }),
    visibility: varchar("visibility", { length: 20 })
      .default("visible")
      .notNull(),
    overrides: jsonb("overrides").$type<PoiFieldOverrides | null>(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    {
      name: "map_poi_overrides_map_poi_unique",
      columns: [table.mapId, table.sourcePoiId],
      unique: true,
    },
  ],
);

export const mapPoiOverridesRelations = relations(
  mapPoiOverrides,
  ({ one }) => ({
    map: one(maps, {
      fields: [mapPoiOverrides.mapId],
      references: [maps.id],
    }),
    sourcePoi: one(pois, {
      fields: [mapPoiOverrides.sourcePoiId],
      references: [pois.id],
    }),
  }),
);

// POI Categories - Dynamic categories per org (shared across all maps in org)
export const poiCategories = pgTable(
  "poi_categories",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // Org-scoped categories (shared across all maps in org)
    orgId: uuid("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    slug: varchar("slug", { length: 50 }).notNull(),
    name: varchar("name", { length: 100 }).notNull(),
    icon: varchar("icon", { length: 50 }).notNull(),
    color: varchar("color", { length: 7 }).notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    isSystem: boolean("is_system").default(false).notNull(),
    isVisible: boolean("is_visible").default(true).notNull(),
    isFilterable: boolean("is_filterable").default(true).notNull(),
    isDefault: boolean("is_default").default(false).notNull(),
    nameTranslations:
      jsonb("name_translations").$type<Record<string, string>>(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    // Unique constraint on (orgId, slug)
    {
      name: "poi_categories_org_slug_unique",
      columns: [table.orgId, table.slug],
      unique: true,
    },
  ],
);

export const poiCategoriesRelations = relations(
  poiCategories,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [poiCategories.orgId],
      references: [organizations.id],
    }),
    pois: many(pois),
  }),
);

// POI Tags - Cross-cutting filter tags (org-scoped)
export const poiTags = pgTable(
  "poi_tags",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    // Org-scoped tags
    orgId: uuid("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    slug: varchar("slug", { length: 50 }).notNull(),
    name: varchar("name", { length: 100 }).notNull(),
    color: varchar("color", { length: 7 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    // Unique constraint on (orgId, slug)
    {
      name: "poi_tags_org_slug",
      columns: [table.orgId, table.slug],
      unique: true,
    },
  ],
);

// POI Tag Assignments - Junction table for POI-tag many-to-many relationship
export const poiTagAssignments = pgTable(
  "poi_tag_assignments",
  {
    poiId: uuid("poi_id")
      .notNull()
      .references(() => pois.id, { onDelete: "cascade" }),
    tagId: uuid("tag_id")
      .notNull()
      .references(() => poiTags.id, { onDelete: "cascade" }),
  },
  (table) => [primaryKey({ columns: [table.poiId, table.tagId] })],
);

export const poiTagsRelations = relations(poiTags, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [poiTags.orgId],
    references: [organizations.id],
  }),
  assignments: many(poiTagAssignments),
}));

export const poiTagAssignmentsRelations = relations(
  poiTagAssignments,
  ({ one }) => ({
    poi: one(pois, {
      fields: [poiTagAssignments.poiId],
      references: [pois.id],
    }),
    tag: one(poiTags, {
      fields: [poiTagAssignments.tagId],
      references: [poiTags.id],
    }),
  }),
);

// Map Labels - Floating text labels on maps
export const mapLabels = pgTable("map_labels", {
  id: uuid("id").primaryKey().defaultRandom(),
  mapId: uuid("map_id")
    .notNull()
    .references(() => maps.id, { onDelete: "cascade" }),
  text: varchar("text", { length: 100 }).notNull(),
  location: geometryColumn("location", "Point"),
  fontSize: integer("font_size").default(14).notNull(),
  fontWeight: varchar("font_weight", { length: 10 })
    .default("normal")
    .notNull(),
  color: varchar("color", { length: 7 }).default("#333333").notNull(),
  rotation: integer("rotation").default(0).notNull(),
  minZoom: integer("min_zoom").default(14).notNull(),
  maxZoom: integer("max_zoom").default(20).notNull(),
  // Label type: 'region' (area labels with bg) or 'fixed' (standard text)
  type: varchar("type", { length: 20 }).default("fixed").notNull(),
  // Text opacity (0-1)
  opacity: real("opacity").default(1).notNull(),
  // Background color for region labels (hex)
  backgroundColor: varchar("background_color", { length: 7 }),
  // Halo width in px for fixed labels
  haloWidth: real("halo_width").default(1.5),
  // Sponsor link (direct FK — labels won't need multi-sponsor)
  sponsorId: uuid("sponsor_id").references(() => mapSponsors.id, {
    onDelete: "set null",
  }),
  showSponsorLogo: boolean("show_sponsor_logo").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: text("created_by").references(() => users.id),
});

export const mapLabelsRelations = relations(mapLabels, ({ one }) => ({
  map: one(maps, {
    fields: [mapLabels.mapId],
    references: [maps.id],
  }),
  creator: one(users, {
    fields: [mapLabels.createdBy],
    references: [users.id],
  }),
  sponsor: one(mapSponsors, {
    fields: [mapLabels.sponsorId],
    references: [mapSponsors.id],
  }),
}));

// Events - Scheduled activities tied to POIs
// poiId uses RESTRICT to prevent orphaned events when POI deleted
export const events = pgTable("events", {
  id: uuid("id").primaryKey().defaultRandom(),
  mapId: uuid("map_id")
    .notNull()
    .references(() => maps.id, { onDelete: "cascade" }),
  poiId: uuid("poi_id").references(() => pois.id, { onDelete: "restrict" }),
  layerId: uuid("layer_id").references(() => layers.id, {
    onDelete: "restrict",
  }),

  // Content
  name: varchar("name", { length: 200 }).notNull(),
  description: text("description"),

  // Schedule
  scheduleType: eventScheduleTypeEnum("schedule_type").notNull(),
  startDate: date("start_date"), // null for permanent
  endDate: date("end_date"), // null for permanent or single-day
  weeklyDays: jsonb("weekly_days").$type<number[]>(), // [0,6] for Sun/Sat (weekly type only)
  startTime: varchar("start_time", { length: 8 }).notNull(), // "HH:MM" or "HH:MM:SS"
  endTime: varchar("end_time", { length: 8 }).notNull(), // "HH:MM" or "HH:MM:SS"

  // V1.1: Headliner support (for concert venues)
  headlinerName: varchar("headliner_name", { length: 200 }),
  headlinerLogoUrl: varchar("headliner_logo_url", { length: 500 }),

  // Link (ticket sales, more info, etc.)
  eventUrl: varchar("event_url", { length: 500 }),
  eventUrlLabel: varchar("event_url_label", { length: 50 }),

  // Status
  isActive: boolean("is_active").default(true).notNull(),

  // Import tracking
  importBatchId: varchar("import_batch_id", { length: 36 }),

  // Metadata
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const eventsRelations = relations(events, ({ one, many }) => ({
  map: one(maps, {
    fields: [events.mapId],
    references: [maps.id],
  }),
  poi: one(pois, {
    fields: [events.poiId],
    references: [pois.id],
  }),
  layer: one(layers, {
    fields: [events.layerId],
    references: [layers.id],
  }),
  sponsorLinks: many(mapSponsorEvents),
}));

// Notifications — visitor-facing operational messages (rain delays, closures, info,
// plus future kinds: rate_visit, welcome, event_reminder, system).
// `kind` discriminator (text, not pg enum per plan 112 decision #3) allows
// additive types without enum churn.
export const notifications = pgTable(
  "notifications",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    kind: text("kind").notNull().default("announcement"),
    title: varchar("title", { length: 100 }).notNull(),
    body: text("body"),
    type: notificationTypeEnum("type").notNull().default("info"),
    isActive: boolean("is_active").default(true).notNull(),
    startsAt: timestamp("starts_at", { withTimezone: true }),
    expiresAt: timestamp("expires_at", { withTimezone: true }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => [index("idx_notifications_map_active").on(t.mapId, t.isActive)],
);

export const notificationsRelations = relations(notifications, ({ one }) => ({
  map: one(maps, {
    fields: [notifications.mapId],
    references: [maps.id],
  }),
}));

// =============================================================================
// Emergency Actions - Per-map quick-action buttons for visitor safety
// =============================================================================

export const mapEmergencyActions = pgTable(
  "map_emergency_actions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    label: varchar("label", { length: 100 }).notNull(),
    labelTranslations: jsonb("label_translations").$type<Record<
      string,
      string
    > | null>(),
    icon: varchar("icon", { length: 50 }).notNull(),
    actionType: emergencyActionTypeEnum("action_type").notNull(),
    target: varchar("target", { length: 255 }).notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => [index("map_emergency_actions_map_idx").on(t.mapId)],
);

export const mapEmergencyActionsRelations = relations(
  mapEmergencyActions,
  ({ one }) => ({
    map: one(maps, {
      fields: [mapEmergencyActions.mapId],
      references: [maps.id],
    }),
  }),
);

// Content Audit Log - Map-scoped content changes (POI edits, category changes, tag changes)
// Used for: Editor history, undo/redo, content versioning
// Retention: May be limited by tier (90 days vs unlimited) - check feature flags
export const contentAuditLog = pgTable(
  "content_audit_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    organizationId: uuid("organization_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    mapId: uuid("map_id").references(() => maps.id, { onDelete: "cascade" }),
    entityType: varchar("entity_type", { length: 50 }).notNull(),
    entityId: uuid("entity_id").notNull(),
    action: auditActionEnum("action").notNull(),
    changes: jsonb("changes"),
    userId: text("user_id").references(() => users.id),
    userEmail: varchar("user_email", { length: 255 }),
    ipAddress: varchar("ip_address", { length: 45 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("audit_log_map_entity_idx").on(
      table.mapId,
      table.entityType,
      table.entityId,
    ),
    index("audit_log_created_at_idx").on(table.createdAt),
    index("audit_log_org_id_idx").on(table.organizationId),
  ],
);

export const contentAuditLogRelations = relations(
  contentAuditLog,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [contentAuditLog.organizationId],
      references: [organizations.id],
    }),
    map: one(maps, {
      fields: [contentAuditLog.mapId],
      references: [maps.id],
    }),
    user: one(users, {
      fields: [contentAuditLog.userId],
      references: [users.id],
    }),
  }),
);

// Security Audit Log - Org-scoped access/mutation events (member invites, role changes, flag changes)
// Used for: Compliance, legal audit trail, superadmin oversight
// Note: Filter superadmin actions when showing to org admins (show as "WonderMaps Support")
export const securityAuditLogs = pgTable(
  "security_audit_logs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    userId: text("user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    orgId: uuid("org_id").references(() => organizations.id, {
      onDelete: "set null",
    }),
    action: text("action").notNull(),
    entityType: text("entity_type").notNull(),
    entityId: uuid("entity_id"),
    metadata: jsonb("metadata"),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("security_audit_logs_org_created_idx").on(
      table.orgId,
      table.createdAt,
    ),
    index("security_audit_logs_entity_idx").on(
      table.entityType,
      table.entityId,
    ),
  ],
);

export const securityAuditLogsRelations = relations(
  securityAuditLogs,
  ({ one }) => ({
    user: one(users, {
      fields: [securityAuditLogs.userId],
      references: [users.id],
    }),
    organization: one(organizations, {
      fields: [securityAuditLogs.orgId],
      references: [organizations.id],
    }),
  }),
);

// Map Versions - Immutable versioning (draft -> published)
export const mapVersions = pgTable("map_versions", {
  id: uuid("id").primaryKey().defaultRandom(),
  mapId: uuid("map_id")
    .notNull()
    .references(() => maps.id, { onDelete: "cascade" }),
  status: mapVersionStatusEnum("status").notNull().default("draft"),
  versionNumber: varchar("version_number", { length: 50 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  publishedAt: timestamp("published_at"),
});

export const mapVersionsRelations = relations(mapVersions, ({ one, many }) => ({
  map: one(maps, {
    fields: [mapVersions.mapId],
    references: [maps.id],
  }),
  layers: many(layers),
  pois: many(pois),
  illustrations: many(mapIllustrations),
}));

// Layer rich content (custom_path, custom_polygon — MVP2)
export interface LayerContent {
  photos?: string[];
  hours?: string;
  links?: Array<{ label: string; url: string }>;
  accessibility?: string;
}

// Layers - Geometry containers
export const layers = pgTable("layers", {
  id: uuid("id").primaryKey().defaultRandom(),
  versionId: uuid("version_id")
    .notNull()
    .references(() => mapVersions.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 255 }).notNull(),
  layerType: layerTypeEnum("layer_type").notNull(),
  // PostGIS geometry for boundaries, zones, buildings (Polygon or LineString)
  // Using "Geometry" type to support multiple geometry types per layer
  geom: geometryColumn("geom", "Geometry"),
  // MapLibre style configuration
  styleJson: jsonb("style_json"),
  sortOrder: integer("sort_order").default(0).notNull(),
  // OSM traceability: "way/123456" - null for manually drawn layers
  osmId: varchar("osm_id", { length: 50 }),
  // MD5 hash of geometry binary — used by publish to skip unchanged layers
  geomHash: text("geom_hash"),
  // Custom path/polygon rich content (MVP2)
  description: text("description"),
  content: jsonb("content").$type<LayerContent>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const layersRelations = relations(layers, ({ one }) => ({
  version: one(mapVersions, {
    fields: [layers.versionId],
    references: [mapVersions.id],
  }),
}));

// ─────────────────────────────────────────────────────────────────────────────
// Custom Map Stickers (plan 120)
//
// Schema is `illustration_*` (broader internal concept — leaves room for
// labels, badges, sponsor logos later). UI/React layer is `Sticker*` —
// the asymmetry is intentional, do not refactor for "consistency".
// ─────────────────────────────────────────────────────────────────────────────

export const ILLUSTRATION_CATEGORIES = [
  "rides",
  "animals",
  "structures",
  "decor",
  "vegetation",
] as const;
export type IllustrationCategory = (typeof ILLUSTRATION_CATEGORIES)[number];

// Curated library of illustration assets (sticker source-of-truth).
// org_id NULL = global library; populated = per-org custom (v2 only).
export const illustrationAssets = pgTable(
  "illustration_assets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    slug: varchar("slug", { length: 100 }).notNull().unique(),
    name: varchar("name", { length: 255 }).notNull(),
    category: varchar("category", { length: 32 }).notNull(),
    tags: jsonb("tags").$type<string[]>().notNull().default([]),
    organizationId: uuid("organization_id").references(() => organizations.id, {
      onDelete: "cascade",
    }),
    baseSize: integer("base_size").notNull(),
    defaultWidthMeters: real("default_width_meters").notNull(),
    defaultMinZoom: real("default_min_zoom").notNull().default(14),
    defaultMaxZoom: real("default_max_zoom").notNull().default(22),
    hidden: boolean("hidden").notNull().default(false),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("illustration_assets_category_idx").on(table.category),
    // NOTE: no index on organizationId in v1 — column is always NULL until v2
    // introduces per-org uploads. A B-tree on an all-NULL column wastes bytes
    // on every insert for zero query benefit. Add the index in the v2 migration.
  ],
);

export const illustrationAssetsRelations = relations(
  illustrationAssets,
  ({ one, many }) => ({
    organization: one(organizations, {
      fields: [illustrationAssets.organizationId],
      references: [organizations.id],
    }),
    instances: many(mapIllustrations),
  }),
);

// Per-map sticker placements. Versioned via map_versions (mirrors layers).
export const mapIllustrations = pgTable(
  "map_illustrations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    versionId: uuid("version_id")
      .notNull()
      .references(() => mapVersions.id, { onDelete: "cascade" }),
    assetId: uuid("asset_id")
      .notNull()
      .references(() => illustrationAssets.id, { onDelete: "restrict" }),
    center: geometryColumn("center", "Point").notNull(),
    widthMeters: real("width_meters").notNull(),
    rotationDeg: real("rotation_deg").notNull().default(0),
    opacity: real("opacity").notNull().default(1),
    // Plan 120 PR9 — nullable. NULL = "use theme.stickers.defaultMinZoom"
    // resolved at render via resolveStickerZoomRange. Override-by-checkbox
    // in StickerEditPanel; new stickers default to null.
    minZoom: real("min_zoom"),
    maxZoom: real("max_zoom"),
    zOrderOverride: real("z_order_override"),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("map_illustrations_version_id_idx").on(table.versionId),
    check(
      "map_illustrations_width_pos",
      sql`${table.widthMeters} > 0`,
    ),
    check(
      "map_illustrations_rotation_range",
      sql`${table.rotationDeg} >= 0 AND ${table.rotationDeg} < 360`,
    ),
    check(
      "map_illustrations_opacity_range",
      sql`${table.opacity} >= 0 AND ${table.opacity} <= 1`,
    ),
    check(
      "map_illustrations_zoom_order",
      sql`${table.minZoom} <= ${table.maxZoom}`,
    ),
    check(
      "map_illustrations_zorder_range",
      sql`${table.zOrderOverride} IS NULL OR (${table.zOrderOverride} >= 0 AND ${table.zOrderOverride} <= 9)`,
    ),
  ],
);

export const mapIllustrationsRelations = relations(
  mapIllustrations,
  ({ one }) => ({
    version: one(mapVersions, {
      fields: [mapIllustrations.versionId],
      references: [mapVersions.id],
    }),
    asset: one(illustrationAssets, {
      fields: [mapIllustrations.assetId],
      references: [illustrationAssets.id],
    }),
  }),
);

// POIs - Points of Interest (belong directly to maps)
export const pois = pgTable(
  "pois",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    name: varchar("name", { length: 255 }).notNull(),
    nameTranslations:
      jsonb("name_translations").$type<Record<string, string>>(),
    // Customer's external reference ID (e.g., from their internal system)
    // Unique per map, used for bulk import matching
    slug: varchar("slug", { length: 100 }),
    externalId: varchar("external_id", { length: 100 }),
    description: text("description"),
    descriptionRich: jsonb("description_rich"), // Lexical JSON AST
    // Dynamic category FK - references poi_categories table
    categoryId: uuid("category_id").references(() => poiCategories.id, {
      onDelete: "set null",
    }),
    // PostGIS Point geometry for location (SRID 4326 = WGS84 GPS coordinates)
    location: geometryColumn("location", "Point"),
    // Flexible properties for icon, hours, etc.
    propertiesJson: jsonb("properties_json"),
    // OSM traceability: "node/123456" - null for manually created POIs
    osmId: varchar("osm_id", { length: 50 }),
    // Rich content fields
    photos: jsonb("photos").$type<POIPhoto[]>().default([]).notNull(),
    hours: jsonb("hours").$type<HoursConfig>(),
    links: jsonb("links").$type<POILink[]>().default([]).notNull(),
    status: poiStatusEnum("status").default("normal").notNull(),
    statusNote: varchar("status_note", { length: 255 }),
    // Season dates for seasonal POIs (only used when status = "seasonal")
    seasonDates: jsonb("season_dates").$type<SeasonDates>(),
    isPublished: boolean("is_published").default(true).notNull(),
    // Sponsor/featured POI fields (premium+ feature)
    sponsorTier: sponsorTierEnum("sponsor_tier"),
    sponsorExpiresAt: timestamp("sponsor_expires_at", { withTimezone: true }),
    // POI-level toggle: admins can hide the rating UI for specific POIs
    ratingsEnabled: boolean("ratings_enabled").default(true).notNull(),
    // Per-POI override for name label rendering on the map.
    // null = follow theme.layers.poiLabel global toggle + zoom range.
    // true = always show (still subject to MapLibre collision detection).
    // false = never show.
    showNameLabel: boolean("show_name_label"),
    // Import tracking - links POI to import batch for undo functionality
    importBatchId: varchar("import_batch_id", { length: 36 }),
    createdBy: text("created_by").references(() => users.id),
    updatedBy: text("updated_by").references(() => users.id),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("pois_map_slug_idx")
      .on(table.mapId, table.slug)
      .where(sql`slug IS NOT NULL`),
  ],
);

export const poisRelations = relations(pois, ({ one, many }) => ({
  map: one(maps, {
    fields: [pois.mapId],
    references: [maps.id],
  }),
  category: one(poiCategories, {
    fields: [pois.categoryId],
    references: [poiCategories.id],
  }),
  tags: many(poiTagAssignments),
  events: many(events),
  curatedListItems: many(curatedListItems),
  feedback: many(visitorFeedback),
  sponsorLinks: many(mapSponsorPois),
}));

// ============================================
// Curated Lists - Admin-created POI collections
// ============================================

export const curatedLists = pgTable(
  "curated_lists",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    name: varchar("name", { length: 255 }).notNull(),
    slug: varchar("slug", { length: 100 }).notNull(),
    description: text("description"),
    isActive: boolean("is_active").default(true).notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("curated_lists_map_slug_idx").on(table.mapId, table.slug),
    index("curated_lists_map_active_idx").on(table.mapId, table.isActive),
  ],
);

export const curatedListsRelations = relations(
  curatedLists,
  ({ one, many }) => ({
    map: one(maps, {
      fields: [curatedLists.mapId],
      references: [maps.id],
    }),
    items: many(curatedListItems),
  }),
);

export const curatedListItems = pgTable(
  "curated_list_items",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    listId: uuid("list_id")
      .notNull()
      .references(() => curatedLists.id, { onDelete: "cascade" }),
    poiId: uuid("poi_id")
      .notNull()
      .references(() => pois.id, { onDelete: "cascade" }),
    sortOrder: integer("sort_order").default(0).notNull(),
    note: varchar("note", { length: 255 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("curated_list_items_list_idx").on(table.listId),
    uniqueIndex("curated_list_items_list_poi_idx").on(
      table.listId,
      table.poiId,
    ),
  ],
);

export const curatedListItemsRelations = relations(
  curatedListItems,
  ({ one }) => ({
    list: one(curatedLists, {
      fields: [curatedListItems.listId],
      references: [curatedLists.id],
    }),
    poi: one(pois, {
      fields: [curatedListItems.poiId],
      references: [pois.id],
    }),
  }),
);

// ============================================
// Map Sponsor Tiers - Custom tier definitions
// ============================================

export const mapSponsorTiers = pgTable(
  "map_sponsor_tiers",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    name: varchar("name", { length: 100 }).notNull(),
    slug: varchar("slug", { length: 50 }).notNull(),
    sortOrder: integer("sort_order").default(0).notNull(),
    maxCount: integer("max_count").default(10),
    // Price for one slot in this tier, in integer cents, USD (plan 127 D1).
    // Null means "not priced yet" — such a tier contributes 0 to open-inventory
    // value and is surfaced as a warning, never silently dropped.
    // Priced per OPERATING SEASON, not per calendar year (D13).
    slotPriceCents: integer("slot_price_cents"),
    sizeBucket: sizeBucketEnum("size_bucket").notNull(),
    color: varchar("color", { length: 7 }),
    showInSplash: boolean("show_in_splash").default(true).notNull(),
    showInWelcome: boolean("show_in_welcome").default(true).notNull(),
    showInHamburger: boolean("show_in_hamburger").default(true).notNull(),
    maxInSplash: integer("max_in_splash").default(2).notNull(),
    maxInWelcome: integer("max_in_welcome").default(2).notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("map_sponsor_tiers_map_slug_idx").on(table.mapId, table.slug),
  ],
);

export const mapSponsorTiersRelations = relations(
  mapSponsorTiers,
  ({ one, many }) => ({
    map: one(maps, {
      fields: [mapSponsorTiers.mapId],
      references: [maps.id],
    }),
    sponsors: many(mapSponsors),
  }),
);

/** Default tiers seeded on map creation */
export const DEFAULT_SPONSOR_TIERS = [
  {
    name: "Gold",
    slug: "gold",
    sortOrder: 30,
    maxCount: 3,
    sizeBucket: "large" as const,
    color: "#FFD700",
    maxInSplash: 1,
    maxInWelcome: 1,
  },
  {
    name: "Silver",
    slug: "silver",
    sortOrder: 20,
    maxCount: 5,
    sizeBucket: "medium" as const,
    color: "#A8A9AD",
    maxInSplash: 2,
    maxInWelcome: 2,
  },
  {
    name: "Bronze",
    slug: "bronze",
    sortOrder: 10,
    maxCount: 10,
    sizeBucket: "small" as const,
    color: "#CD7F32",
    maxInSplash: 0,
    maxInWelcome: 0,
  },
];

// ============================================
// Map Sponsors - Brand logo placements (v4)
// ============================================

export const mapSponsors = pgTable(
  "map_sponsors",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    name: varchar("name", { length: 255 }).notNull(),
    logoPublicId: varchar("logo_public_id", { length: 255 }),
    logoVersion: varchar("logo_version", { length: 50 }),
    websiteUrl: varchar("website_url", { length: 500 }),
    tierId: uuid("tier_id")
      .notNull()
      .references(() => mapSponsorTiers.id),
    sortOrder: integer("sort_order").default(0).notNull(),
    isActive: boolean("is_active").default(true).notNull(),
    isHeadline: boolean("is_headline").default(false).notNull(),
    showName: boolean("show_name").default(true).notNull(),
    expiresAt: timestamp("expires_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("map_sponsors_map_active_idx").on(table.mapId, table.isActive),
    index("map_sponsors_tier_id_idx").on(table.tierId),
  ],
);

export const mapSponsorsRelations = relations(mapSponsors, ({ one, many }) => ({
  map: one(maps, {
    fields: [mapSponsors.mapId],
    references: [maps.id],
  }),
  tier: one(mapSponsorTiers, {
    fields: [mapSponsors.tierId],
    references: [mapSponsorTiers.id],
  }),
  sponsorPois: many(mapSponsorPois),
  sponsorEvents: many(mapSponsorEvents),
  sponsoredLabels: many(mapLabels),
}));

// ============================================
// Sponsor ↔ POI / Event join tables
// ============================================

export const mapSponsorPois = pgTable(
  "map_sponsor_pois",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sponsorId: uuid("sponsor_id")
      .notNull()
      .references(() => mapSponsors.id, { onDelete: "cascade" }),
    poiId: uuid("poi_id")
      .notNull()
      .references(() => pois.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("map_sponsor_pois_sponsor_poi_idx").on(
      table.sponsorId,
      table.poiId,
    ),
    index("map_sponsor_pois_poi_idx").on(table.poiId),
  ],
);

export const mapSponsorPoisRelations = relations(
  mapSponsorPois,
  ({ one }) => ({
    sponsor: one(mapSponsors, {
      fields: [mapSponsorPois.sponsorId],
      references: [mapSponsors.id],
    }),
    poi: one(pois, {
      fields: [mapSponsorPois.poiId],
      references: [pois.id],
    }),
  }),
);

export const mapSponsorEvents = pgTable(
  "map_sponsor_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sponsorId: uuid("sponsor_id")
      .notNull()
      .references(() => mapSponsors.id, { onDelete: "cascade" }),
    eventId: uuid("event_id")
      .notNull()
      .references(() => events.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("map_sponsor_events_sponsor_event_idx").on(
      table.sponsorId,
      table.eventId,
    ),
    index("map_sponsor_events_event_idx").on(table.eventId),
  ],
);

export const mapSponsorEventsRelations = relations(
  mapSponsorEvents,
  ({ one }) => ({
    sponsor: one(mapSponsors, {
      fields: [mapSponsorEvents.sponsorId],
      references: [mapSponsors.id],
    }),
    event: one(events, {
      fields: [mapSponsorEvents.eventId],
      references: [events.id],
    }),
  }),
);

// ============================================
// Sponsor Pitch Reports (plan 127 C)
//
// A venue sells sponsorships BEFORE it has any map traffic. Eric at Quassy is
// pitching 2027 slots in 2026 using his 2026 season numbers — ticket scans,
// concessions, web views — which live in his systems, not ours. So a pitch
// report is built from operator-supplied inputs plus visible arithmetic, not
// from our analytics.
//
// A prospect is NOT a sponsor. mapSponsors rows render on the live visitor map;
// a prospect being pitched must never appear there. Hence a separate table
// rather than a status flag on mapSponsors — every existing sponsor read stays
// a place a prospect cannot leak from.
//
// Access model (differs from the usual token pattern on purpose):
//   - urlToken is stored in PLAINTEXT. The admin table lists every report with
//     a copyable link, so it must be re-readable. It is a capability URL, not a
//     credential.
//   - passwordHash is bcrypt and nullable. Null means the link is public.
//     The password is the credential; it is never stored in the clear.
// ============================================

export const sponsorPitchReports = pgTable(
  "sponsor_pitch_reports",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    orgId: uuid("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    // Prospect being pitched — deliberately free text + its own logo fields,
    // NOT a mapSponsors FK. Pepsi does not exist as a sponsor until they sign.
    prospectName: varchar("prospect_name", { length: 255 }).notNull(),
    prospectLogoPublicId: varchar("prospect_logo_public_id", { length: 255 }),
    prospectLogoVersion: varchar("prospect_logo_version", { length: 50 }),
    // Which tier is being pitched. Nullable so a report can be drafted before
    // the tier is decided; set null on tier delete rather than cascading the
    // whole report away.
    tierId: uuid("tier_id").references(() => mapSponsorTiers.id, {
      onDelete: "set null",
    }),
    // "2026 Season", "Apr-Oct 2026" — operator's label for the period their
    // input numbers describe. Free text because venues describe seasons wildly
    // differently and none of it is computed on.
    periodLabel: varchar("period_label", { length: 120 }).notNull(),
    // Operator-supplied venue figures + the projection assumptions. jsonb
    // because these fields will churn as we learn what venues actually have —
    // same reasoning as organizations.settings.
    inputs: jsonb("inputs").$type<SponsorPitchInputs>().notNull(),
    // Capability URL. Plaintext by design — see the access model note above.
    urlToken: varchar("url_token", { length: 64 }).notNull(),
    // bcrypt. Null = public link, no password gate.
    passwordHash: varchar("password_hash", { length: 255 }),
    // Null = never expires, which is the v1 policy. The column exists now
    // because adding it costs nothing before db:push and a migration after
    // (D18). Staleness in v1 is handled by LABELLING, not access control.
    expiresAt: timestamp("expires_at"),
    // "Figures as of <date>" — rendered on the report, mandatory (D18).
    // Deliberately NOT updatedAt: fixing a typo in `notes` must not imply the
    // venue restated its numbers. Bumped only when `inputs` actually changes.
    figuresAsOf: timestamp("figures_as_of").defaultNow().notNull(),
    // Per-report view telemetry (D17 boundary, D22). Answers the one question a
    // negotiation actually turns on: was the link opened, or is silence just
    // silence? Table stakes for any shared document.
    //
    // THE BOUNDARY: these two columns are per-report only. Anything that
    // aggregates across prospects, ranks them, or schedules follow-ups is the
    // CRM that D17 rules out.
    lastViewedAt: timestamp("last_viewed_at"),
    viewCount: integer("view_count").default(0).notNull(),
    createdByUserId: text("created_by_user_id").references(() => users.id, {
      onDelete: "set null",
    }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("sponsor_pitch_reports_url_token_idx").on(table.urlToken),
    index("sponsor_pitch_reports_map_idx").on(table.mapId),
  ],
);

export const sponsorPitchReportsRelations = relations(
  sponsorPitchReports,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [sponsorPitchReports.orgId],
      references: [organizations.id],
    }),
    map: one(maps, {
      fields: [sponsorPitchReports.mapId],
      references: [maps.id],
    }),
    tier: one(mapSponsorTiers, {
      fields: [sponsorPitchReports.tierId],
      references: [mapSponsorTiers.id],
    }),
  }),
);

// Import Snapshots - Audit trail for import undo functionality
// Tracks import batches so POIs can be deleted by importBatchId
export const importSnapshots = pgTable(
  "import_snapshots",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    orgId: uuid("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    // Number of POIs imported in this batch
    importedCount: integer("imported_count").notNull(),
    // UUID linking to POIs created in this import
    importBatchId: varchar("import_batch_id", { length: 36 }).notNull(),
  },
  (table) => [
    index("import_snapshots_map_created_idx").on(table.mapId, table.createdAt),
  ],
);

export const importSnapshotsRelations = relations(
  importSnapshots,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [importSnapshots.orgId],
      references: [organizations.id],
    }),
    map: one(maps, {
      fields: [importSnapshots.mapId],
      references: [maps.id],
    }),
  }),
);

// ============================================
// Better Auth Tables
// ============================================

// User preferences type for notification settings
export interface UserPreferences {
  notifications?: {
    productUpdates?: boolean;
    supportReplies?: boolean;
    analyticsDigest?: boolean; // undefined = opted in, false = opted out
  };
}

// Users - Authentication users
export const users = pgTable("user", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  emailVerified: boolean("email_verified").notNull().default(false),
  image: text("image"),
  preferences: jsonb("preferences").$type<UserPreferences>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  deletedAt: timestamp("deleted_at"),
  lastLoginAt: timestamp("last_login_at"),
});

export const usersRelations = relations(users, ({ many }) => ({
  sessions: many(sessions),
  accounts: many(accounts),
  memberships: many(organizationMembers),
}));

// Sessions - User sessions for authentication
export const sessions = pgTable("session", {
  id: text("id").primaryKey(),
  expiresAt: timestamp("expires_at").notNull(),
  token: text("token").notNull().unique(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  userId: text("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
});

export const sessionsRelations = relations(sessions, ({ one }) => ({
  user: one(users, {
    fields: [sessions.userId],
    references: [users.id],
  }),
}));

// Accounts - OAuth/credential accounts linked to users
export const accounts = pgTable("account", {
  id: text("id").primaryKey(),
  accountId: text("account_id").notNull(),
  providerId: text("provider_id").notNull(),
  userId: text("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  idToken: text("id_token"),
  accessTokenExpiresAt: timestamp("access_token_expires_at"),
  refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
  scope: text("scope"),
  password: text("password"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const accountsRelations = relations(accounts, ({ one }) => ({
  user: one(users, {
    fields: [accounts.userId],
    references: [users.id],
  }),
}));

// Verification - Email verification and password reset tokens
export const verifications = pgTable("verification", {
  id: text("id").primaryKey(),
  identifier: text("identifier").notNull(),
  value: text("value").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// ============================================
// Organization Membership
// ============================================

// Organization Members - Links users to organizations with roles
export const organizationMembers = pgTable("organization_members", {
  id: uuid("id").primaryKey().defaultRandom(),
  userId: text("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  organizationId: uuid("organization_id")
    .notNull()
    .references(() => organizations.id, { onDelete: "cascade" }),
  role: memberRoleEnum("role").notNull().default("viewer"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const organizationMembersRelations = relations(
  organizationMembers,
  ({ one }) => ({
    user: one(users, {
      fields: [organizationMembers.userId],
      references: [users.id],
    }),
    organization: one(organizations, {
      fields: [organizationMembers.organizationId],
      references: [organizations.id],
    }),
  }),
);

// Organization Invites - Invite codes for joining organizations
export const organizationInvites = pgTable("organization_invites", {
  id: uuid("id").primaryKey().defaultRandom(),
  organizationId: uuid("organization_id")
    .notNull()
    .references(() => organizations.id, { onDelete: "cascade" }),
  code: varchar("code", { length: 20 }).notNull().unique(),
  role: memberRoleEnum("role").notNull().default("viewer"),
  createdBy: text("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at"),
  maxUses: integer("max_uses"),
  useCount: integer("use_count").default(0).notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  // Email invite fields
  email: varchar("email", { length: 255 }), // null = link invite
  inviteType: varchar("invite_type", { length: 20 }).default("link").notNull(), // "email" | "link"
  invitedByEmail: varchar("invited_by_email", { length: 255 }), // audit trail
  sentAt: timestamp("sent_at"), // when email was sent
  redeemedAt: timestamp("redeemed_at"), // when invite was used
  redeemedBy: text("redeemed_by").references(() => users.id), // who redeemed it
});

export const organizationInvitesRelations = relations(
  organizationInvites,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [organizationInvites.organizationId],
      references: [organizations.id],
    }),
    creator: one(users, {
      fields: [organizationInvites.createdBy],
      references: [users.id],
    }),
  }),
);

// ============================================
// Support System
// ============================================

// Support request type enum
export const supportRequestTypeEnum = pgEnum("support_request_type", [
  "support",
  "feature",
  "bug",
  "inquiry",
]);

// Support request status enum
export const supportRequestStatusEnum = pgEnum("support_request_status", [
  "open",
  "in_progress",
  "closed",
]);

// Support request metadata type
export interface SupportRequestMetadata {
  browser?: string;
  currentUrl?: string;
  userAgent?: string;
}

// Support requests table
export const supportRequests = pgTable("support_requests", {
  id: uuid("id").defaultRandom().primaryKey(),
  userId: text("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  orgId: uuid("org_id").references(() => organizations.id, {
    onDelete: "set null",
  }),
  type: supportRequestTypeEnum("type").notNull().default("support"),
  status: supportRequestStatusEnum("status").notNull().default("open"),
  subject: varchar("subject", { length: 255 }).notNull(),
  description: text("description").notNull(),
  metadata: jsonb("metadata").$type<SupportRequestMetadata>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const supportRequestsRelations = relations(
  supportRequests,
  ({ one }) => ({
    user: one(users, {
      fields: [supportRequests.userId],
      references: [users.id],
    }),
    organization: one(organizations, {
      fields: [supportRequests.orgId],
      references: [organizations.id],
    }),
  }),
);

// =============================================================================
// Visitor Feedback
// =============================================================================

export const feedbackTypeEnum = pgEnum("feedback_type", [
  "gps_exit_survey",
  "poi_review",
  "general",
  "post_event",
]);

export const visitorFeedback = pgTable(
  "visitor_feedback",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    orgId: uuid("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    mapId: uuid("map_id")
      .notNull()
      .references(() => maps.id, { onDelete: "cascade" }),
    poiId: uuid("poi_id").references(() => pois.id, { onDelete: "set null" }),
    feedbackType: feedbackTypeEnum("feedback_type")
      .notNull()
      .default("gps_exit_survey"),
    rating: integer("rating").notNull(), // 1-5
    comment: text("comment"),
    tags: varchar("tags", { length: 255 }), // comma-separated tag keys
    deviceType: varchar("device_type", { length: 10 }),
    gpsSessionMinutes: integer("gps_session_minutes"),
    poisViewed: integer("pois_viewed"),
    ipHash: varchar("ip_hash", { length: 64 }), // HMAC-SHA256, no raw IP
    archivedAt: timestamp("archived_at"),
    flaggedAt: timestamp("flagged_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("visitor_feedback_org_id_idx").on(table.orgId),
    index("visitor_feedback_org_created_idx").on(table.orgId, table.createdAt),
    index("visitor_feedback_map_created_idx").on(table.mapId, table.createdAt),
    index("visitor_feedback_ip_map_idx").on(table.ipHash, table.mapId),
    index("visitor_feedback_poi_created_idx").on(table.poiId, table.createdAt),
  ],
);

export const visitorFeedbackRelations = relations(
  visitorFeedback,
  ({ one }) => ({
    organization: one(organizations, {
      fields: [visitorFeedback.orgId],
      references: [organizations.id],
    }),
    map: one(maps, {
      fields: [visitorFeedback.mapId],
      references: [maps.id],
    }),
    poi: one(pois, {
      fields: [visitorFeedback.poiId],
      references: [pois.id],
    }),
  }),
);

// QR Codes - Manually uploaded styled QR codes per channel (standard + custom)
export const qrCodes = pgTable(
  "qr_codes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    organizationId: uuid("organization_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    channel: varchar("channel", { length: 50 }).notNull(),
    cloudinaryPublicId: varchar("cloudinary_public_id", { length: 255 }),
    cloudinaryVersion: varchar("cloudinary_version", { length: 50 }),
    uploadedAt: timestamp("uploaded_at").defaultNow().notNull(),
    uploadedBy: text("uploaded_by").references(() => users.id),
    // Per-channel redirect config
    redirectMode: varchar("redirect_mode", { length: 20 })
      .default("default")
      .notNull(),
    redirectValue: varchar("redirect_value", { length: 500 }),
    // Custom channel fields
    isCustom: boolean("is_custom").default(false).notNull(),
    name: varchar("name", { length: 100 }),
    description: varchar("description", { length: 255 }),
  },
  (table) => [
    uniqueIndex("qr_codes_org_channel_idx").on(
      table.organizationId,
      table.channel,
    ),
  ],
);

export const qrCodesRelations = relations(qrCodes, ({ one }) => ({
  organization: one(organizations, {
    fields: [qrCodes.organizationId],
    references: [organizations.id],
  }),
  uploader: one(users, {
    fields: [qrCodes.uploadedBy],
    references: [users.id],
  }),
}));

// Custom domains for multi-tenant support (premium tier)
export const customDomains = pgTable("custom_domains", {
  id: uuid("id").primaryKey().defaultRandom(),
  organizationId: uuid("organization_id")
    .notNull()
    .references(() => organizations.id, { onDelete: "cascade" }),
  domain: varchar("domain", { length: 255 }).notNull().unique(),
  status: varchar("status", { length: 50 }).notNull().default("pending_dns"),
  cfPagesHostnameId: varchar("cf_pages_hostname_id", { length: 255 }),
  dnsVerifiedAt: timestamp("dns_verified_at"),
  activatedAt: timestamp("activated_at"),
  lastCheckedAt: timestamp("last_checked_at"),
  lastError: varchar("last_error", { length: 500 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const customDomainsRelations = relations(customDomains, ({ one }) => ({
  organization: one(organizations, {
    fields: [customDomains.organizationId],
    references: [organizations.id],
  }),
}));

// ============================================
// Report Send Log (metrics email idempotency)
// ============================================

export const reportSendLog = pgTable(
  "report_send_log",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    organizationId: uuid("organization_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    periodStart: timestamp("period_start").notNull(),
    periodEnd: timestamp("period_end").notNull(),
    frequency: varchar("frequency", { length: 10 }).notNull(),
    recipientCount: integer("recipient_count").notNull(),
    status: varchar("status", { length: 10 }).notNull().default("sent"), // sent, skipped, failed
    errorMessage: text("error_message"),
    sentAt: timestamp("sent_at").defaultNow().notNull(),
  },
  (table) => [
    // Unique constraint for idempotency
    {
      name: "report_send_log_org_freq_period_unique",
      columns: [table.organizationId, table.frequency, table.periodStart],
      unique: true,
    },
    index("idx_report_send_log_org").on(table.organizationId),
    index("idx_report_send_log_status").on(table.status),
  ],
);

export const reportSendLogRelations = relations(reportSendLog, ({ one }) => ({
  organization: one(organizations, {
    fields: [reportSendLog.organizationId],
    references: [organizations.id],
  }),
}));

// ============================================
// Feature Flags
// ============================================

export const featureFlags = pgTable("feature_flags", {
  id: uuid("id").primaryKey().defaultRandom(),
  key: varchar("key", { length: 100 }).notNull().unique(),
  name: varchar("name", { length: 255 }).notNull(),
  description: text("description"),
  flagType: varchar("flag_type", { length: 20 }).notNull().default("boolean"),
  enabled: boolean("enabled").notNull().default(false),
  percentage: integer("percentage"),
  variants: jsonb("variants"),
  requiredTier: varchar("required_tier", { length: 20 }),
  allowOrgOverride: boolean("allow_org_override").notNull().default(false),
  category: varchar("category", { length: 50 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: text("created_by").references(() => users.id),
  deletedAt: timestamp("deleted_at"),
});

export const featureFlagsRelations = relations(
  featureFlags,
  ({ one, many }) => ({
    creator: one(users, {
      fields: [featureFlags.createdBy],
      references: [users.id],
    }),
    orgOverrides: many(featureFlagOrgOverrides),
    roleOverrides: many(featureFlagRoleOverrides),
  }),
);

// Feature Flag Org Overrides - Per-org exceptions to global feature flags
export const featureFlagOrgOverrides = pgTable(
  "feature_flag_org_overrides",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    flagId: uuid("flag_id")
      .notNull()
      .references(() => featureFlags.id, { onDelete: "cascade" }),
    orgId: uuid("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    enabled: boolean("enabled"),
    percentage: integer("percentage"),
    variant: varchar("variant", { length: 50 }),
    reason: text("reason"),
    expiresAt: timestamp("expires_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
    createdBy: text("created_by").references(() => users.id),
  },
  (table) => [
    {
      name: "feature_flag_org_overrides_flag_org_unique",
      columns: [table.flagId, table.orgId],
      unique: true,
    },
  ],
);

export const featureFlagOrgOverridesRelations = relations(
  featureFlagOrgOverrides,
  ({ one }) => ({
    flag: one(featureFlags, {
      fields: [featureFlagOrgOverrides.flagId],
      references: [featureFlags.id],
    }),
    organization: one(organizations, {
      fields: [featureFlagOrgOverrides.orgId],
      references: [organizations.id],
    }),
    creator: one(users, {
      fields: [featureFlagOrgOverrides.createdBy],
      references: [users.id],
    }),
  }),
);

// Feature Flag Role Overrides - Per-org, per-role exceptions to feature flags
// Allows "let owners in Org X test polygon tool" use cases
export const featureFlagRoleOverrides = pgTable(
  "feature_flag_role_overrides",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    flagId: uuid("flag_id")
      .notNull()
      .references(() => featureFlags.id, { onDelete: "cascade" }),
    orgId: uuid("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    role: memberRoleEnum("role").notNull(),
    enabled: boolean("enabled").notNull().default(true),
    reason: text("reason"),
    expiresAt: timestamp("expires_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    createdBy: text("created_by").references(() => users.id),
  },
  (table) => [
    {
      name: "feature_flag_role_overrides_flag_org_role_unique",
      columns: [table.flagId, table.orgId, table.role],
      unique: true,
    },
  ],
);

export const featureFlagRoleOverridesRelations = relations(
  featureFlagRoleOverrides,
  ({ one }) => ({
    flag: one(featureFlags, {
      fields: [featureFlagRoleOverrides.flagId],
      references: [featureFlags.id],
    }),
    organization: one(organizations, {
      fields: [featureFlagRoleOverrides.orgId],
      references: [organizations.id],
    }),
    creator: one(users, {
      fields: [featureFlagRoleOverrides.createdBy],
      references: [users.id],
    }),
  }),
);

// =============================================================================
// Leads (ROI Calculator)
// =============================================================================

export const leads = pgTable("leads", {
  id: uuid("id").primaryKey().defaultRandom(),
  name: varchar("name", { length: 200 }).notNull(),
  email: varchar("email", { length: 320 }).notNull(),
  role: varchar("role", { length: 100 }).notNull(),
  venueName: varchar("venue_name", { length: 200 }),
  venueType: varchar("venue_type", { length: 100 }),
  totalValue: integer("total_value"),
  roi: real("roi"),
  calculatorState: jsonb("calculator_state"),
  source: varchar("source", { length: 50 }).default("roi-calculator"),
  ipHash: varchar("ip_hash", { length: 64 }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

// Type exports for use across the monorepo
export type Organization = typeof organizations.$inferSelect;
export type NewOrganization = typeof organizations.$inferInsert;

export type Map = typeof maps.$inferSelect;
export type NewMap = typeof maps.$inferInsert;

export type MapPoiOverride = typeof mapPoiOverrides.$inferSelect;
export type NewMapPoiOverride = typeof mapPoiOverrides.$inferInsert;

export type PoiCategory = typeof poiCategories.$inferSelect;
export type NewPoiCategory = typeof poiCategories.$inferInsert;

export type PoiTag = typeof poiTags.$inferSelect;
export type NewPoiTag = typeof poiTags.$inferInsert;

export type MapLabel = typeof mapLabels.$inferSelect;
export type NewMapLabel = typeof mapLabels.$inferInsert;

export type Event = typeof events.$inferSelect;
export type NewEvent = typeof events.$inferInsert;

export type ContentAuditLog = typeof contentAuditLog.$inferSelect;
export type NewContentAuditLog = typeof contentAuditLog.$inferInsert;
// Backwards compat alias (deprecated - use ContentAuditLog)
export type AuditLog = ContentAuditLog;
export type NewAuditLog = NewContentAuditLog;

export type MapVersion = typeof mapVersions.$inferSelect;
export type NewMapVersion = typeof mapVersions.$inferInsert;

export type Layer = typeof layers.$inferSelect;
export type NewLayer = typeof layers.$inferInsert;

export type POI = typeof pois.$inferSelect;
export type NewPOI = typeof pois.$inferInsert;

export type User = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;

export type Session = typeof sessions.$inferSelect;
export type NewSession = typeof sessions.$inferInsert;

export type Account = typeof accounts.$inferSelect;
export type NewAccount = typeof accounts.$inferInsert;

export type Verification = typeof verifications.$inferSelect;
export type NewVerification = typeof verifications.$inferInsert;

export type OrganizationMember = typeof organizationMembers.$inferSelect;
export type NewOrganizationMember = typeof organizationMembers.$inferInsert;

export type BaseTheme = typeof baseThemes.$inferSelect;
export type NewBaseTheme = typeof baseThemes.$inferInsert;

export type Theme = typeof themes.$inferSelect;
export type NewTheme = typeof themes.$inferInsert;

export type SupportRequest = typeof supportRequests.$inferSelect;
export type NewSupportRequest = typeof supportRequests.$inferInsert;

export type OrganizationInvite = typeof organizationInvites.$inferSelect;
export type NewOrganizationInvite = typeof organizationInvites.$inferInsert;

export type CustomDomain = typeof customDomains.$inferSelect;
export type NewCustomDomain = typeof customDomains.$inferInsert;

export type ReportSendLog = typeof reportSendLog.$inferSelect;
export type NewReportSendLog = typeof reportSendLog.$inferInsert;

export type SecurityAuditLog = typeof securityAuditLogs.$inferSelect;
export type NewSecurityAuditLog = typeof securityAuditLogs.$inferInsert;

export type FeatureFlag = typeof featureFlags.$inferSelect;
export type NewFeatureFlag = typeof featureFlags.$inferInsert;

export type FeatureFlagOrgOverride =
  typeof featureFlagOrgOverrides.$inferSelect;
export type NewFeatureFlagOrgOverride =
  typeof featureFlagOrgOverrides.$inferInsert;

export type FeatureFlagRoleOverride =
  typeof featureFlagRoleOverrides.$inferSelect;
export type NewFeatureFlagRoleOverride =
  typeof featureFlagRoleOverrides.$inferInsert;

export type QRCode = typeof qrCodes.$inferSelect;
export type NewQRCode = typeof qrCodes.$inferInsert;

export type ImportSnapshot = typeof importSnapshots.$inferSelect;
export type NewImportSnapshot = typeof importSnapshots.$inferInsert;

export type MapSponsorTier = typeof mapSponsorTiers.$inferSelect;
export type NewMapSponsorTier = typeof mapSponsorTiers.$inferInsert;

export type MapSponsor = typeof mapSponsors.$inferSelect;
export type NewMapSponsor = typeof mapSponsors.$inferInsert;

export type MapSponsorPoi = typeof mapSponsorPois.$inferSelect;
export type MapSponsorEvent = typeof mapSponsorEvents.$inferSelect;

export type SponsorPitchReport = typeof sponsorPitchReports.$inferSelect;
export type NewSponsorPitchReport = typeof sponsorPitchReports.$inferInsert;

// Backwards compat alias for table (deprecated - use contentAuditLog)
export { contentAuditLog as auditLog };

export type MemberRole = "owner" | "editor" | "viewer";
export type OrgType = "zoo" | "garden" | "fair" | "park" | "default";
export type OrgTier = (typeof ORG_TIERS)[number];
export type SupportRequestType = "support" | "feature" | "bug" | "inquiry";
export type SupportRequestStatus = "open" | "in_progress" | "closed";
export type POIStatus = "normal" | "temp_closed" | "seasonal";

export type VisitorFeedback = typeof visitorFeedback.$inferSelect;
export type NewVisitorFeedback = typeof visitorFeedback.$inferInsert;
export type FeedbackType =
  | "gps_exit_survey"
  | "poi_review"
  | "general"
  | "post_event";

// =============================================================================
// Help Articles
// =============================================================================

export const helpArticles = pgTable("help_articles", {
  id: uuid("id").primaryKey().defaultRandom(),
  slug: text("slug").notNull().unique(),
  title: text("title").notNull(),
  icon: text("icon").notNull(),
  description: text("description").notNull(),
  contentRich: jsonb("content_rich"),
  contentText: text("content_text"),
  videoUrl: text("video_url"),
  sortOrder: integer("sort_order").notNull().default(0),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedBy: text("updated_by").references(() => users.id, {
    onDelete: "set null",
  }),
});

export type HelpArticle = typeof helpArticles.$inferSelect;
export type NewHelpArticle = typeof helpArticles.$inferInsert;

export type MapEmergencyAction = typeof mapEmergencyActions.$inferSelect;
export type NewMapEmergencyAction = typeof mapEmergencyActions.$inferInsert;
export type EmergencyActionType = "call" | "navigate" | "category";

// =============================================================================
// Promote Assets
// =============================================================================

export const promoteAssets = pgTable(
  "promote_assets",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    orgId: uuid("org_id")
      .notNull()
      .references(() => organizations.id, { onDelete: "cascade" }),
    title: text("title").notNull(),
    description: text("description"),
    category: text("category").notNull(), // sponsors, print, marketing_kit, guides
    subcategory: text("subcategory"), // free-text sub-grouping (e.g. "sticker", "social_instagram")
    assetType: text("asset_type").notNull(), // canva_link, pdf, pptx, google_doc, external_link, image
    url: text("url").notNull(),
    thumbnailPublicId: text("thumbnail_public_id"), // Cloudinary public ID for preview
    thumbnailVersion: text("thumbnail_version"), // Cloudinary version string
    mapId: uuid("map_id").references(() => maps.id, { onDelete: "set null" }),
    sortOrder: integer("sort_order").notNull().default(0),
    isReady: boolean("is_ready").notNull().default(false),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (t) => [index("idx_promote_assets_org").on(t.orgId)],
);

export type PromoteAsset = typeof promoteAssets.$inferSelect;
export type NewPromoteAsset = typeof promoteAssets.$inferInsert;

// =============================================================================
// Map Scans (Mobile Readability Scanner)
// =============================================================================

export const mapScans = pgTable("map_scans", {
  id: uuid("id").defaultRandom().primaryKey(),
  slug: varchar("slug", { length: 128 }).notNull().unique(),

  // Venue info
  venueName: varchar("venue_name", { length: 256 }).notNull(),
  venueCity: varchar("venue_city", { length: 128 }),
  venueUrl: text("venue_url"),
  contactName: varchar("contact_name", { length: 128 }),
  personalNote: text("personal_note"),

  // Analysis results
  score: integer("score").notNull(),
  grade: varchar("grade", { length: 2 }).notNull(),
  summary: text("summary").notNull(),
  issues: jsonb("issues").notNull().$type<ScanIssue[]>(),
  strengths: jsonb("strengths").$type<ScanStrength[]>(),
  sectionScores: jsonb("section_scores").notNull().$type<SectionScores>(),
  rawAnalysis: jsonb("raw_analysis").notNull().$type<RawAnalysis>(),

  // Images
  originalImageUrl: text("original_image_url").notNull(),

  // CTA
  ctaUrl: text("cta_url"),
  ctaText: varchar("cta_text", { length: 128 }),

  // Analytics
  viewCount: integer("view_count").default(0).notNull(),
  lastViewedAt: timestamp("last_viewed_at"),

  // Meta
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: text("created_by").references(() => users.id),
});

// Map Scan types
export type ScanSource =
  | "legibility"
  | "wayfinding"
  | "information_architecture"
  | "accessibility"
  | "mobile_ux"
  | "custom";

export type ScanStrength = {
  source: ScanSource;
  title: string;
  description: string;
  hidden?: boolean;
  custom?: boolean;
};

export type ScanIssue = {
  source: ScanSource;
  category: string;
  severity: "critical" | "warning" | "info";
  title: string;
  description: string;
  recommendation: string;
  region: string;
  hidden?: boolean;
  custom?: boolean;
};

export type SectionScores = {
  legibility: number;
  wayfinding: number;
  information_architecture: number;
  accessibility: number;
  mobile_ux: number;
};

export type RawAnalysis = {
  legibility: unknown;
  wayfinding: unknown;
  information_architecture: unknown;
  accessibility: unknown;
  mobile_ux: unknown;
  failedPersonas: string[];
  model: string;
  timestamp: string;
  totalTokensUsed: number;
};

export type MapScan = typeof mapScans.$inferSelect;
export type NewMapScan = typeof mapScans.$inferInsert;
